/**
 * Magic-link consume (V1 auth-profiles-gdpr) — the `${appOrigin}/magic-link?token=…`
 * destination from every sign-in / account-exists email. POST
 * /auth/magic-link/consume issues a session (clicking proves inbox ownership,
 * #80b — it also verifies a pending email), then routes by lifecycle.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resolveCopy } from "@humble/shared";
import { Card, IconBrokenHeart } from "../../design/index.js";
import { api, getDeviceFp, isApiError } from "../../lib/api.js";
import { setSession } from "../../lib/store.js";
import { clearSignupFlow, routeForLifecycle } from "./signup-flow.js";
import "./auth.css";

export function MagicLinkScreen() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // single-use token — StrictMode must not consume twice
    (async () => {
      try {
        const pair = await api.post("/auth/magic-link/consume", {
          body: { token, device_fp: getDeviceFp() },
        });
        if (!pair?.access_token) throw new Error("no token");
        clearSignupFlow();
        setSession(pair.access_token, pair.user ?? null, pair.lifecycle ?? null);
        navigate(routeForLifecycle(pair.lifecycle));
      } catch (err) {
        setError(
          isApiError(err, "TOKEN_INVALID")
            ? (resolveCopy("auth.link_expired") ?? err.message)
            : isApiError(err)
              ? err.message
              : "Network error — try the link again.",
        );
      }
    })();
  }, [token, navigate]);

  return (
    <div className="auth auth--center" data-screen="S-01-magic">
      <div className="auth__brand">
        <IconBrokenHeart width={24} height={24} style={{ color: "var(--accent)" }} />
        humble
      </div>
      <Card padding="lg">
        {!token ? (
          <>
            <h1 className="auth__title">Missing link token</h1>
            <p className="text-dim">Open the link from your email directly.</p>
            <Link to="/" className="btn btn--primary btn--block">
              Back to sign in
            </Link>
          </>
        ) : error ? (
          <>
            <h1 className="auth__title">That link didn't work</h1>
            <p className="field__error" role="alert">
              {error}
            </p>
            <Link to="/" className="btn btn--primary btn--block">
              Back to sign in
            </Link>
          </>
        ) : (
          <p role="status">Signing you in…</p>
        )}
      </Card>
    </div>
  );
}
